import React, { useEffect, useState, useCallback } from 'react';
import { Download, BarChart3, DollarSign, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { gov } from '../../lib/govApi';
import { toast } from '../../hooks/use-toast';

const currentYear = new Date().getFullYear();
const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;

const GovReports = () => {
  const [year, setYear] = useState(currentYear);
  const [quarter, setQuarter] = useState(currentQuarter);
  const [fee, setFee] = useState(null);
  const [annual, setAnnual] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [feeRes, annualRes] = await Promise.all([
        gov.reportFee(year, quarter),
        gov.reportAnnual(year),
      ]);
      setFee(feeRes.data);
      setAnnual(annualRes.data);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load reports', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [year, quarter]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const downloadQuarterly = async () => {
    try {
      const res = await gov.reportQuarterly(year, quarter);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `DES-Quarterly-${year}-Q${quarter}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Downloaded' });
    } catch (e) {
      toast({ title: 'Download failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-5" data-testid="gov-reports-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2"><BarChart3 className="h-6 w-6 text-blue-700" /> DES Reports</h1>
        <p className="text-sm text-slate-500">Quarterly sales · Vendor Management Fee · Annual contract utilization</p>
      </div>

      <Card className="border-2">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-slate-500 block">Year</label>
            <Input type="number" className="w-32" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="report-year" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block">Quarter</label>
            <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
              <SelectTrigger className="w-32" data-testid="report-quarter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Q1 (Jan–Mar)</SelectItem>
                <SelectItem value="2">Q2 (Apr–Jun)</SelectItem>
                <SelectItem value="3">Q3 (Jul–Sep)</SelectItem>
                <SelectItem value="4">Q4 (Oct–Dec)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="bg-blue-700 hover:bg-blue-800 text-white" onClick={downloadQuarterly} data-testid="download-quarterly-xlsx">
            <Download className="h-4 w-4 mr-1" /> Quarterly Excel
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-2">
            <CardContent className="p-5">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-2"><DollarSign className="h-5 w-5 text-emerald-600" /> Vendor Management Fee — Q{quarter} {year}</h2>
              <div className="text-3xl font-bold text-emerald-700">${(fee?.management_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div className="text-sm text-slate-500 mt-1">
                Total sales: <b>${(fee?.total_sales || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</b> · Rate: <b>{((fee?.fee_rate || 0.015) * 100).toFixed(2)}%</b> · Orders: <b>{fee?.order_count || 0}</b>
              </div>
              <p className="text-xs text-slate-400 mt-2">Payable to Washington State DES at end of quarter.</p>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardContent className="p-5">
              <h2 className="font-semibold text-slate-800 mb-2">Annual Contract Utilization — {year}</h2>
              <div className="text-2xl font-bold text-blue-700">${(annual?.total_sales || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div className="text-xs text-slate-500">{annual?.order_count || 0} orders · ${(annual?.vendor_management_fee || 0).toFixed(2)} mgmt fee</div>
              <div className="mt-4">
                <div className="text-xs font-semibold text-slate-600 mb-1">Top Agencies</div>
                <ul className="space-y-1">
                  {(annual?.by_agency || []).slice(0, 5).map((row) => (
                    <li key={row.agency} className="flex justify-between text-sm">
                      <span className="text-slate-700">{row.agency}</span>
                      <span className="font-semibold text-blue-700">${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </li>
                  ))}
                  {(annual?.by_agency || []).length === 0 && <li className="text-slate-400 text-xs">No data yet.</li>}
                </ul>
              </div>
              <div className="mt-3">
                <div className="text-xs font-semibold text-slate-600 mb-1">Top Products</div>
                <ul className="space-y-1">
                  {(annual?.by_product || []).slice(0, 5).map((row) => (
                    <li key={row.product} className="flex justify-between text-sm">
                      <span className="text-slate-700 truncate mr-2">{row.product}</span>
                      <span className="font-semibold text-blue-700">${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </li>
                  ))}
                  {(annual?.by_product || []).length === 0 && <li className="text-slate-400 text-xs">No data yet.</li>}
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GovReports;
